import { useQuery } from '@tanstack/react-query';
import { IndividualsDuplexerChannel } from '@wix/ai-scheduling-assistant-common-lib/commonTypes';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { API_BASE } from '../api-base';
import { apiFetch } from '../api-client';
import { queryClient, queryKeys } from '../query-client';
import { useMe } from '../me';
import { useMyAgents } from '../my-agents';
import { useTasks } from '../tasks';
import { findGoal, useWorkspaceGoals } from '../goals';
import { useDuplexerSubscription } from '../use-duplexer-subscription';
import { calendarOwnerKey, readCalendarStore, writeCalendarStore } from './calendar-storage';
import type { CalendarItem, CalendarItemKind, CalendarRange } from './calendar-item';
import type { CalendarRoutine } from './calendar-normalize';
import {
  bookingLayers,
  bookingSitesOf,
  bookingsFromAnswer,
  calendarItemsInRange,
  calendarLayers,
  failedSiteNames,
  meetingLayers,
  meetingsFromAnswer,
  type Bookings,
  type BookingsAnswer,
  type BookingsSite,
  type CalendarCompleteness,
  type CalendarLayer,
  type Meetings,
  type MeetingsAnswer,
} from './calendar-sources';

// The pure half lives in `calendar-sources.ts`; re-exported so the screen's imports stay where they were.
export { layerOf } from './calendar-sources';
export type { BookingsSite, CalendarLayer, Meetings, WireAccount } from './calendar-sources';

// The Calendar screen's reads. Each source is its own query, so a slow one (a Nylas-backed account, a
// Bookings site) never holds back the others: every layer draws as soon as it lands. The connected
// calendars and Bookings come drawn from the harness (`/api/calendar/*`, the same routes the phones
// read); routines come with their runs from the routines read; tasks from the tasks read the app shares.

const REFRESH_MS = 5 * 60_000;
const MEETINGS_LIVE_MS = 60_000;
const BOOKINGS_LIVE_MS = 2 * 60_000;
/** How long a reply waits for its second copy and for the replies right after it. */
const REPLY_SETTLE_MS = 2_000;
/** The fewest reads replies can cause: a worker agent's conversation pushes a reply at every step. */
const REPLY_READ_GAP_MS = 15_000;

export interface CalendarSourceStatus {
  kind: CalendarItemKind;
  loading: boolean;
  completeness: CalendarCompleteness;
  reconnectRequired?: boolean;
  permissionDenied?: boolean;
  /** Accounts or sites that did not answer. */
  failed: string[];
}

export interface CalendarData {
  items: CalendarItem[];
  bookingSites: BookingsSite[];
  layers: CalendarLayer[];
  /** Every outside source has answered, so `layers` is what the account has now, not what it had. */
  settledLayers: boolean;
  /** Outside sources still on their first answer. */
  pendingKinds: CalendarItemKind[];
  /** Sources still reading the period on screen — on first load, or after stepping to a new period. */
  loadingKinds: CalendarItemKind[];
  sources: CalendarSourceStatus[];
  /** No calendar account is connected — the "Connect your calendar" state. */
  noAccounts: boolean;
  refetch: () => void;
}

export interface CalendarAccount {
  id: string;
  email: string;
  provider: string;
}

async function fetchAccounts(): Promise<CalendarAccount[]> {
  const res = await apiFetch(`${API_BASE}/api/calendars`, { credentials: 'include' });
  if (!res.ok) throw new Error(`calendars ${res.status}`);
  const data = (await res.json()) as { calendars?: CalendarAccount[] };
  return data.calendars ?? [];
}

async function readCalendarRoute<T>(route: 'meetings' | 'bookings', range: CalendarRange): Promise<T> {
  const query = new URLSearchParams({ from: new Date(range.from).toISOString(), to: new Date(range.to).toISOString() });
  const res = await apiFetch(`${API_BASE}/api/calendar/${route}?${query}`, { credentials: 'include' });
  if (!res.ok) throw new Error(`calendar ${route} ${res.status}`);
  return (await res.json()) as T;
}

/**
 * Every connected account's meetings, with what could not be read kept from `previousAnswer` (the rules are
 * `meetingsFromAnswer`'s).
 */
export async function fetchMeetings(range: CalendarRange, previousAnswer?: Meetings): Promise<Meetings> {
  return meetingsFromAnswer(await readCalendarRoute<MeetingsAnswer>('meetings', range), previousAnswer);
}

/**
 * Every site's Bookings sessions, with an incomplete refresh keeping the last complete one (the rules are
 * `bookingsFromAnswer`'s). The failed sites come back by name, as the screen's notice names them.
 */
export async function fetchBookings(range: CalendarRange, previousAnswer?: Bookings): Promise<Bookings> {
  const bookings = bookingsFromAnswer(await readCalendarRoute<BookingsAnswer>('bookings', range), previousAnswer);
  return { ...bookings, failed: failedSiteNames(bookings) };
}

/** The routines in scope, each with its runs inside `range`. */
export async function fetchRoutineRuns(workspaceId: string | undefined, range: CalendarRange): Promise<CalendarRoutine[]> {
  const routines: CalendarRoutine[] = [];
  const seen = new Set<string>();
  let pages = 0;
  let cursor: string | undefined;
  do {
    if (++pages > 100) throw new Error('routines page limit');
    const res = await apiFetch(`${API_BASE}/api/routines`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...(workspaceId ? { routineWorkspaceIds: [workspaceId] } : {}),
        runsFrom: new Date(range.from).toISOString(),
        runsTo: new Date(range.to).toISOString(),
        ...(cursor ? { cursor } : {}),
      }),
    });
    if (!res.ok) throw new Error(`routines ${res.status}`);
    const data = (await res.json()) as { routines?: CalendarRoutine[]; cursor?: string };
    routines.push(...(data.routines ?? []).filter((routine) => !!routine?.id));
    const next = data.cursor || undefined;
    if (!next) break;
    if (seen.has(next)) throw new Error('routines repeated cursor');
    seen.add(next);
    cursor = next;
  } while (cursor);
  return routines;
}

/** The connected calendar accounts — one read the screen, its card and every item's mark share. */
export function useCalendarAccounts() {
  const ownerKey = calendarOwnerKey(useMe());
  return useQuery({ queryKey: queryKeys.calendarAccounts(ownerKey), queryFn: fetchAccounts, enabled: !!ownerKey, staleTime: REFRESH_MS });
}

// What the calendar showed last time, kept so a reopened calendar draws its layers at once instead of
// building them up source by source. Names, logos and colours only — never an event.
const LAYERS_STORE = 'layers';

/**
 * Everything on the calendar inside `range`. `workspaceId` narrows the Symphony layers (tasks, routines)
 * to one space; the connected calendars and Bookings belong to the account and show in every space.
 * `neighbours` are prefetched so stepping a week or a month paints from cache.
 */
export function useCalendarItems(
  range: CalendarRange,
  workspaceId: string | undefined,
  { neighbours = [], live = false, showEventTitles = true }: { neighbours?: CalendarRange[]; live?: boolean; showEventTitles?: boolean } = {},
): CalendarData {
  const ownerKey = calendarOwnerKey(useMe());
  const accounts = useCalendarAccounts();
  const accountList = accounts.data ?? [];

  const meetings = useQuery({
    queryKey: queryKeys.calendarMeetingsInRange(ownerKey, showEventTitles, range),
    queryFn: () => fetchMeetings(range, queryClient.getQueryData<Meetings>(queryKeys.calendarMeetingsInRange(ownerKey, showEventTitles, range))),
    enabled: accountList.length > 0,
    keepPreviousData: true,
    staleTime: REFRESH_MS,
    // Back on the tab means back from Google or Wix, where the change was just made: read again at once.
    refetchOnWindowFocus: live ? 'always' : true,
    // Live while the calendar is on screen: a meeting added in Google shows up within a minute. React
    // Query skips the interval while the tab is hidden.
    refetchInterval: live ? MEETINGS_LIVE_MS : false,
  });

  // Maestro changes a calendar from the chat, and its reply is written after the change is made: re-read
  // when a reply lands rather than waiting for the next poll. One read serves a reply's copies (the harness,
  // then the message consumer) and replies close behind it, and it restarts a read already under way, which
  // may have started before the change. The settle window and gap bound provider reads.
  const replyRead = useRef<{ timer?: ReturnType<typeof setTimeout>; last: number }>({ last: 0 });
  useEffect(() => () => clearTimeout(replyRead.current.timer), []);
  useDuplexerSubscription<{ role?: string }>(IndividualsDuplexerChannel.ASSISTANT_CHAT, (payload) => {
    const pending = replyRead.current;
    if (!live || payload.entity?.role !== 'ASSISTANT' || pending.timer) return;
    pending.timer = setTimeout(() => {
      pending.timer = undefined;
      pending.last = Date.now();
      void queryClient.invalidateQueries({ queryKey: queryKeys.calendar, refetchType: 'active' });
    }, Math.max(REPLY_SETTLE_MS, pending.last + REPLY_READ_GAP_MS - Date.now()));
  });

  const bookings = useQuery({
    queryKey: queryKeys.calendarBookings(ownerKey, range),
    queryFn: () => fetchBookings(range, queryClient.getQueryData<Bookings>(queryKeys.calendarBookings(ownerKey, range))),
    keepPreviousData: true,
    staleTime: REFRESH_MS,
    refetchOnWindowFocus: live ? 'always' : true,
    // Slower than meetings: Wix rate-limits the reads, and a booking rarely lands in the next minute.
    refetchInterval: live ? BOOKINGS_LIVE_MS : false,
  });

  const rememberedLayers = useMemo<CalendarLayer[]>(
    () => (typeof window === 'undefined' ? [] : readCalendarStore<CalendarLayer[]>(LAYERS_STORE, ownerKey) ?? []),
    [ownerKey],
  );

  const tasks = useTasks(workspaceId);
  const goals = useWorkspaceGoals();
  const agents = useMyAgents();
  // Work that belongs to no space is the owner's all the same, so a space's calendar shows it too.
  const accountTasks = useTasks(undefined, { enabled: !!workspaceId });
  const routines = useQuery({
    queryKey: queryKeys.calendarRoutineRuns(workspaceId, range),
    queryFn: () => fetchRoutineRuns(workspaceId, range),
    keepPreviousData: true,
    staleTime: REFRESH_MS,
  });
  const accountRoutines = useQuery({
    queryKey: queryKeys.calendarRoutineRuns(undefined, range),
    queryFn: () => fetchRoutineRuns(undefined, range),
    enabled: !!workspaceId,
    keepPreviousData: true,
    staleTime: REFRESH_MS,
  });

  const neighbourKey = neighbours.map((n) => `${n.from}-${n.to}`).join(',');
  // Only once the period on screen has landed, so the prefetch never competes with it for Wix's rate limit.
  const settled = !meetings.isFetching && !bookings.isFetching;
  useEffect(() => {
    if (!settled) return;
    for (const neighbour of neighbours) {
      if (accountList.length > 0) {
        void queryClient.prefetchQuery({
          queryKey: queryKeys.calendarMeetingsInRange(ownerKey, showEventTitles, neighbour),
          queryFn: () => fetchMeetings(neighbour),
          staleTime: REFRESH_MS,
        });
      }
      void queryClient.prefetchQuery({
        queryKey: queryKeys.calendarBookings(ownerKey, neighbour),
        queryFn: () => fetchBookings(neighbour),
        staleTime: REFRESH_MS,
      });
    }
    // The neighbours are recomputed every render; their bounds are what matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settled, neighbourKey, accountList.length, ownerKey, showEventTitles]);

  const data = useMemo(() => {
    const bookingSites = bookingSitesOf(bookings.data?.sites);
    const items = calendarItemsInRange({
      range,
      workspaceId,
      meetings: (meetings.data?.items ?? []).map((item) => (showEventTitles ? item : { ...item, title: '', busy: true })),
      bookingSites,
      tasks: tasks.state.kind === 'ready' ? tasks.state.tasks : undefined,
      accountTasks: accountTasks.state.kind === 'ready' ? accountTasks.state.tasks : [],
      routines: routines.data,
      accountRoutines: accountRoutines.data ?? [],
      goals: goals.status === 'ready' ? goals.goals : undefined,
      agentNameOf: (agentId) => (agents.status === 'ready' ? agents.team.agents.find((agent) => agent.id === agentId)?.name : undefined),
      goalTitleOf: (goalId) => (goals.status === 'ready' ? findGoal(goals.goals, goalId)?.title : undefined),
    });
    const meetingsPending = accountList.length === 0 ? accounts.isLoading : !meetings.data;
    const bookingsPending = !bookings.data;
    const layers = calendarLayers({
      meetings: meetingsPending ? undefined : meetingLayers(meetings.data?.accounts ?? []),
      bookings: bookingsPending ? undefined : bookingLayers(bookingSites),
      remembered: rememberedLayers,
    });

    const sources: CalendarSourceStatus[] = [
      {
        kind: 'meeting',
        loading: accounts.isLoading || (accountList.length > 0 && meetings.isLoading),
        completeness: accounts.isError || meetings.isError ? 'failed' : meetings.data?.completeness ?? 'complete',
        reconnectRequired: meetings.data?.accounts.some((account) => account.reconnectRequired) ?? false,
        failed: accounts.isError ? ['calendars'] : meetings.isError ? accountList.map((a) => a.email) : meetings.data?.failed ?? [],
      },
      {
        kind: 'booking',
        loading: bookings.isLoading,
        completeness: bookings.isError ? 'failed' : bookings.data?.completeness ?? 'complete',
        permissionDenied: bookings.data?.permissionDenied ?? false,
        failed: bookings.isError ? ['bookings'] : bookings.data?.failed ?? [],
      },
      // Inside a Space the account-wide reads bring the work that belongs to no Space; without them it would go missing quietly.
      {
        kind: 'task',
        loading: tasks.state.kind === 'loading' || (!!workspaceId && accountTasks.state.kind === 'loading'),
        completeness: tasks.state.kind === 'error' || (!!workspaceId && accountTasks.state.kind === 'error') ? 'failed' : 'complete',
        failed: tasks.state.kind === 'error' || (!!workspaceId && accountTasks.state.kind === 'error') ? ['tasks'] : [],
      },
      {
        kind: 'routine',
        loading: routines.isLoading || (!!workspaceId && accountRoutines.isLoading),
        completeness: routines.isError || (!!workspaceId && accountRoutines.isError) ? 'failed' : 'complete',
        failed: routines.isError || (!!workspaceId && accountRoutines.isError) ? ['routines'] : [],
      },
      {
        kind: 'goal',
        loading: goals.status === 'loading',
        completeness: goals.status === 'error' ? 'failed' : 'complete',
        failed: goals.status === 'error' ? ['goals'] : [],
      },
    ];

    return {
      items,
      bookingSites,
      layers,
      settledLayers: !meetingsPending && !bookingsPending,
      pendingKinds: [...(meetingsPending ? ['meeting' as const] : []), ...(bookingsPending ? ['booking' as const] : [])],
      loadingKinds: [
        ...(accounts.isLoading || (accountList.length > 0 && (meetings.isLoading || meetings.isPreviousData)) ? ['meeting' as const] : []),
        ...(bookings.isLoading || bookings.isPreviousData ? ['booking' as const] : []),
        ...(tasks.state.kind === 'loading' ? ['task' as const] : []),
        ...(routines.isLoading || routines.isPreviousData ? ['routine' as const] : []),
        ...(goals.status === 'loading' ? ['goal' as const] : []),
      ],
      sources,
      noAccounts: accounts.isSuccess && accountList.length === 0,
      refetch: () => {
        void accounts.refetch();
        void meetings.refetch();
        void bookings.refetch();
        void tasks.refresh();
        void routines.refetch();
        void queryClient.invalidateQueries({ queryKey: queryKeys.workspaceGoals(undefined) });
        if (workspaceId) {
          void accountTasks.refresh();
          void accountRoutines.refetch();
        }
      },
    };
    // The query objects change identity every render; their data and states are what the result reads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    range.from,
    range.to,
    showEventTitles,
    accounts.data,
    accounts.status,
    meetings.data,
    meetings.status,
    meetings.isPreviousData,
    bookings.isPreviousData,
    bookings.data,
    bookings.status,
    tasks.state,
    accountTasks.state,
    routines.data,
    routines.status,
    routines.isPreviousData,
    accountRoutines.data,
    accountRoutines.status,
    goals,
    agents,
    rememberedLayers,
  ]);

  useEffect(() => {
    if (data.settledLayers)
      writeCalendarStore(
        LAYERS_STORE,
        ownerKey,
        data.layers.filter((layer) => layer.kind === 'meeting' || layer.kind === 'booking'),
      );
  }, [data.settledLayers, data.layers, ownerKey]);

  return data;
}

/** Why a move did not happen: the harness's stable code, `changed` when the meeting moved elsewhere first. */
export class MeetingMoveError extends Error {
  constructor(readonly code: string) {
    super(`calendar move ${code}`);
  }
}

/**
 * Moves a meeting to new times, as a drag on the grid asks. It moves on screen at once and goes back if
 * the provider refuses; either way the period is read again after, so the grid ends on what is true. The
 * times the grid drew it at go along, so a meeting moved elsewhere since is refused instead of overwritten.
 */
export function useMoveMeeting(): (item: CalendarItem, start: number, end: number) => Promise<void> {
  const ownerKey = calendarOwnerKey(useMe());
  return useCallback(
    async (item: CalendarItem, start: number, end: number) => {
      const meetingQueries = { queryKey: queryKeys.calendarMeetings(ownerKey) };
      const place = (from: number, to: number) =>
        queryClient.setQueriesData<Meetings>(meetingQueries, (data) =>
          data ? { ...data, items: data.items.map((each) => (each.key === item.key ? { ...each, start: from, end: to } : each)) } : data,
        );
      place(start, end);
      try {
        const res = await apiFetch(`${API_BASE}/api/calendar/move`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tokenId: item.ref.tokenId,
            calendarId: item.ref.calendarId,
            eventId: item.ref.eventId,
            fromStart: new Date(item.start).toISOString(),
            fromEnd: new Date(item.end).toISOString(),
            start: new Date(start).toISOString(),
            end: new Date(end).toISOString(),
          }),
        });
        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as { error?: unknown };
          throw new MeetingMoveError(typeof body.error === 'string' ? body.error : String(res.status));
        }
      } catch (error) {
        place(item.start, item.end);
        throw error;
      } finally {
        void queryClient.invalidateQueries(meetingQueries);
      }
    },
    [ownerKey],
  );
}
